/* eslint-disable react-hooks/exhaustive-deps */

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { useMediaQuery } from '#app/hooks/use-media-query.tsx'
import { useGlobalChatStore } from '#app/routes/chat+/index.tsx'
import Chat from './components/chat-overview.tsx'
import MobileSiderbar from './components/mobile-sidebar.tsx'
import Sidebar from './components/sidebar.tsx'

function Chatscreen({ data }: { data: any }) {
	const { t } = useTranslation('chat')
	const menuIsVisible = useGlobalChatStore(state => state.menuIsVisible)
	const dispatch = useGlobalChatStore(useShallow(state => state.dispatch))
	const isDesktop = useMediaQuery('(min-width: 760px)')

	const [desktopMenu, setDesktopMenu] = useState(true)

	const sidebarStyle = {
		transition: 'transform 0.3s ease-out',
		transform: desktopMenu ? 'translateX(0)' : 'translateX(-100%)',
	}

	useEffect(() => {
		dispatch({
			type: 'ADD_CONVERSATION_LIST',
			payload: data.data,
			meta: data.meta,
			typeOfDispatch: 'create',
		})
	}, [])

	useEffect(() => {
		if (isDesktop) {
			dispatch({ type: 'SET_IS_MENU_VISIBLE', isVisible: false })
		}
	}, [isDesktop])

	return (
		<main className="relative flex h-dvh w-full overflow-hidden">
			{menuIsVisible ? <MobileSiderbar /> : null}
			<div className="relative lg:flex">
				<div
					className="dark hidden flex-shrink-0 border border-gray-300 bg-[#ebeae8] shadow-2xl lg:flex lg:w-[320px] lg:flex-col"
					style={sidebarStyle}
				>
					<div className="flex h-full min-h-0 flex-col">
						<Sidebar />
					</div>
				</div>
				<button
					className="hidden lg:block"
					style={{
						position: 'absolute',
						top: '50%',
						left: desktopMenu ? '320px' : '0',
						transform: 'translateY(-50%)',
						zIndex: 100,
						transition: 'left 0.3s ease-out, opacity 0.3s ease-out',
					}}
					aria-label="Toggle Sidebar"
					onClick={() => {
						setDesktopMenu(!desktopMenu)
					}}
				>
					{desktopMenu ? (
						<ChevronWithHover />
					) : (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<ChevronRight
										className="h-auto w-7 text-primary-400"
										strokeWidth={4}
									/>
								</TooltipTrigger>
								<TooltipContent className="bg-black text-white">
									<p>{t('intake.closeSidebar')}</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}
				</button>
			</div>
			<div
				className={`lg:-ml-320px flex max-w-full flex-1 flex-grow flex-col`}
				style={{
					marginLeft: isDesktop ? (desktopMenu ? '' : '-320px') : '0px',
					transition: 'margin-left 0.3s ease-out',
				}}
			>
				<Chat />
			</div>
		</main>
	)
}

export default memo(Chatscreen)

export function ChevronWithHover() {
	const { t } = useTranslation('chat')
	const [isHovered, setIsHovered] = useState(false)

	const LineIcon = () => (
		<svg
			className="h-auto w-7"
			viewBox="0 0 40 40"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M20 5V35"
				stroke="#192d50"
				strokeWidth="8"
				strokeLinecap="round"
			/>
		</svg>
	)

	return (
		<button
			aria-label="Toggle Sidebar"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			style={{
				position: 'absolute',
				top: '50%',
				transform: 'translateY(-50%)',
				marginLeft: '1rem',
				zIndex: 100,
			}}
		>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						{isHovered ? (
							<ChevronLeft
								className="h-auto w-8 text-primary-400 transition-all duration-300"
								strokeWidth="4"
							/>
						) : (
							<LineIcon />
						)}
					</TooltipTrigger>
					<TooltipContent className="bg-black text-white">
						<p>{t('intake.openSidebar')}</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</button>
	)
}
